import {upperFirst} from "@pg-mono/nodash/src/string/upper_first";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {getHouseVariantsSectionTexts} from "./common_texts/get_house_variants_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const houseOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        housesPriceMin: props.region.stats?.properties_houses_ranges_price_min || 0,
        housesCountReady: props.region.stats?.properties_houses_count_ready || 0,
        housesCountReadyInThreeMonths: props.region.stats?.properties_houses_count_ready_in_3m || 0,
        housesCountReadyInSixMonths: props.region.stats?.properties_houses_count_ready_in_6m || 0,
        housesCountReadyInOneYear: props.region.stats?.properties_houses_count_ready_in_12m || 0,
        housesTopVendors: props.region.stats?.properties_houses_top_vendors || [],
        housesLatestConstructionDate: props.region.stats?.properties_houses_latest_construction_date || null,
        housesOffersCountForSale: props.region.stats?.offers_houses_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        propertiesHousesRangesAreaMin: props.region.stats?.properties_houses_ranges_area_min || 0,
        propertiesHousesRangesAreaMax: props.region.stats?.properties_houses_ranges_area_max || 0
    };

    if (!variables.housesPropertiesCountForSale || !variables.housesOffersCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - dom w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.housesPropertiesCountForSale)} oferta domu na sprzedaż.`,
                            `dostępne są ${numberFormat(variables.housesPropertiesCountForSale)} oferty domów na sprzedaż.`,
                            `dostępnych jest ${numberFormat(variables.housesPropertiesCountForSale)} ofert domów na sprzedaż.`
                        ])(variables.housesPropertiesCountForSale)} Domy w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesHousesRangesAreaMax !== variables.propertiesHousesRangesAreaMin
                                ? pluralize([
                                      `${areaFormat(variables.propertiesHousesRangesAreaMin)}`,
                                      `od ${variables.propertiesHousesRangesAreaMin} do ${areaFormat(variables.propertiesHousesRangesAreaMax)}`,
                                      `od ${variables.propertiesHousesRangesAreaMin} do ${areaFormat(variables.propertiesHousesRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesHousesRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowy dom w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [`Najtańszy dom w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.housesPriceMin, {unit: "euro"})}.`]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem mieszkania w Hiszpanii? Poznaj `,
                            variables.flatsPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-mieszkania`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę mieszkań w ${variables.regionName}.`
                                  }
                                : `ofertę mieszkań ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }
    return [
        {
            headline: `Domy na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.housesOffersCountForSale,
                    propertiesCountForSale: variables.housesPropertiesCountForSale,
                    offerTypeName: "nowych domów"
                }),
                ...getHouseVariantsSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztuje nowy dom ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańszy nowy dom ${variables.regionNameDeclensionWhere} dostępny w sprzedaży kosztuje ${priceFormat(variables.housesPriceMin)}.`,
                        ` `,
                        variables.averagePriceM2 > 0
                            ? `Aktualnie średnia cena za m2 domów ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
                            : ""
                    ]
                    // TODO: waiting to next stage
                    // getEstimatedPriceText(props.selectedRegionEstimatedPrices)
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach domów ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na domy.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - domy gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.housesCountReady,
                    offersCountReadyInThreeMonths: variables.housesCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.housesCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.housesCountReadyInOneYear,
                    offerTypeNamePluralize: ["dom", "domy", "domów"],
                    offerTypeNameWithDeclensionOfReadyPluralize: ["dom zostanie oddany", "domy zostaną oddane", "domów zostanie oddanych"],
                    offersLatestConstructionDate: variables.housesLatestConstructionDate
                })
            ]
        },
        {
            headline: `Domy ${variables.regionNameDeclensionWhere} - udogodnienia`,
            paragraphs: [
                // TODO: next stage
                // {
                //     type: ParagraphType.TEXT,
                //     text: `Domy ${variables.regionNameDeclensionWhere} posiadają:`
                // },
                // {
                //     type: ParagraphType.LIST,
                //     text: [
                //         `garaże - $[x] ${pluralize(["inwestycja", "inwestycje", "inwestycji"])(0)}`,
                //         `miejsca postojowe naziemne - $[x] ${pluralize(["inwestycja", "inwestycje", "inwestycji"])(0)}.`
                //     ]
                // },
                ...(variables.housesTopVendors?.length > 0
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `Najwięcej nowych domów ${variables.regionNameDeclensionWhere} ${pluralize(["oferuje", "oferują: ", "oferują: "])(
                                  variables.housesTopVendors.length
                              )} ${variables.housesTopVendors.map((vendor) => vendor.name).join(", ")}.`
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),

                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Jesteś zainteresowany zakupem mieszkania? Poznaj `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }

        // TODO: waiting to next stage with POI information
        // {
        //     headline: `${variables.regionName} - infrastruktura miejska`,
        //     paragraphs: [
        //         {
        //             type: ParagraphType.TEXT,
        //             text: `Nowe domy ${variables.regionName} - szukając odpowiedniej nieruchomości warto zwrócić uwagę na okolicę i poziom rozbudowania infrastruktury miejskiej. Liczba placówek edukacyjnych, zdrowotnych i rekreacyjnych ma kluczowe znaczenie dla osób zainteresowanych zakupem nowego domu. ${upperFirst(variables.regionNameDeclensionWhere)} znajdziesz:`
        //         }
        //         ...getLocalInfrastructureSectionTexts({poiCount: {}})
        //     ]
        // }
    ];
};
import {upperFirst} from "@pg-mono/nodash/src/string/upper_first";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {getHouseVariantsSectionTexts} from "./common_texts/get_house_variants_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const houseOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        housesPriceMin: props.region.stats?.properties_houses_ranges_price_min || 0,
        housesCountReady: props.region.stats?.properties_houses_count_ready || 0,
        housesCountReadyInThreeMonths: props.region.stats?.properties_houses_count_ready_in_3m || 0,
        housesCountReadyInSixMonths: props.region.stats?.properties_houses_count_ready_in_6m || 0,
        housesCountReadyInOneYear: props.region.stats?.properties_houses_count_ready_in_12m || 0,
        housesTopVendors: props.region.stats?.properties_houses_top_vendors || [],
        housesLatestConstructionDate: props.region.stats?.properties_houses_latest_construction_date || null,
        housesOffersCountForSale: props.region.stats?.offers_houses_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        propertiesHousesRangesAreaMin: props.region.stats?.properties_houses_ranges_area_min || 0,
        propertiesHousesRangesAreaMax: props.region.stats?.properties_houses_ranges_area_max || 0
    };

    if (!variables.housesPropertiesCountForSale || !variables.housesOffersCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - dom w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.housesPropertiesCountForSale)} oferta domu na sprzedaż.`,
                            `dostępne są ${numberFormat(variables.housesPropertiesCountForSale)} oferty domów na sprzedaż.`,
                            `dostępnych jest ${numberFormat(variables.housesPropertiesCountForSale)} ofert domów na sprzedaż.`
                        ])(variables.housesPropertiesCountForSale)} Domy w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesHousesRangesAreaMax !== variables.propertiesHousesRangesAreaMin
                                ? pluralize([
                                      `${areaFormat(variables.propertiesHousesRangesAreaMin)}`,
                                      `od ${variables.propertiesHousesRangesAreaMin} do ${areaFormat(variables.propertiesHousesRangesAreaMax)}`,
                                      `od ${variables.propertiesHousesRangesAreaMin} do ${areaFormat(variables.propertiesHousesRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesHousesRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowy dom w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [`Najtańszy dom w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.housesPriceMin, {unit: "euro"})}.`]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem mieszkania w Hiszpanii? Poznaj `,
                            variables.flatsPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-mieszkania`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę mieszkań w ${variables.regionName}.`
                                  }
                                : `ofertę mieszkań ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }
    return [
        {
            headline: `Domy na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.housesOffersCountForSale,
                    propertiesCountForSale: variables.housesPropertiesCountForSale,
                    offerTypeName: "nowych domów"
                }),
                ...getHouseVariantsSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztuje nowy dom ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańszy nowy dom ${variables.regionNameDeclensionWhere} dostępny w sprzedaży kosztuje ${priceFormat(variables.housesPriceMin)}.`,
                        ` `,
                        variables.averagePriceM2 > 0
                            ? `Aktualnie średnia cena za m2 domów ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
                            : ""
                    ]
                    // TODO: waiting to next stage
                    // getEstimatedPriceText(props.selectedRegionEstimatedPrices)
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach domów ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na domy.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - domy gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.housesCountReady,
                    offersCountReadyInThreeMonths: variables.housesCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.housesCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.housesCountReadyInOneYear,
                    offerTypeNamePluralize: ["dom", "domy", "domów"],
                    offerTypeNameWithDeclensionOfReadyPluralize: ["dom zostanie oddany", "domy zostaną oddane", "domów zostanie oddanych"],
                    offersLatestConstructionDate: variables.housesLatestConstructionDate
                })
            ]
        },
        {
            headline: `Domy ${variables.regionNameDeclensionWhere} - udogodnienia`,
            paragraphs: [
                // TODO: next stage
                // {
                //     type: ParagraphType.TEXT,
                //     text: `Domy ${variables.regionNameDeclensionWhere} posiadają:`
                // },
                // {
                //     type: ParagraphType.LIST,
                //     text: [
                //         `garaże - $[x] ${pluralize(["inwestycja", "inwestycje", "inwestycji"])(0)}`,
                //         `miejsca postojowe naziemne - $[x] ${pluralize(["inwestycja", "inwestycje", "inwestycji"])(0)}.`
                //     ]
                // },
                ...(variables.housesTopVendors?.length > 0
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `Najwięcej nowych domów ${variables.regionNameDeclensionWhere} ${pluralize(["oferuje", "oferują: ", "oferują: "])(
                                  variables.housesTopVendors.length
                              )} ${variables.housesTopVendors.map((vendor) => vendor.name).join(", ")}.`
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),

                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Jesteś zainteresowany zakupem mieszkania? Poznaj `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }

        // TODO: waiting to next stage with POI information
        // {
        //     headline: `${variables.regionName} - infrastruktura miejska`,
        //     paragraphs: [
        //         {
        //             type: ParagraphType.TEXT,
        //             text: `Nowe domy ${variables.regionName} - szukając odpowiedniej nieruchomości warto zwrócić uwagę na okolicę i poziom rozbudowania infrastruktury miejskiej. Liczba placówek edukacyjnych, zdrowotnych i rekreacyjnych ma kluczowe znaczenie dla osób zainteresowanych zakupem nowego domu. ${upperFirst(variables.regionNameDeclensionWhere)} znajdziesz:`
        //         }
        //         ...getLocalInfrastructureSectionTexts({poiCount: {}})
        //     ]
        // }
    ];
};
